import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { CartesianGrid, Line, LineChart, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api, query } from '../../api/client'
import type { RatingRow } from '../../api/types'
import { formatCount, formatDate } from '../../lib/format'
import { RangePicker, rangeFrom, type RangeKey } from '../RangePicker'

/** Average rating and rating count over time for one country, with price changes marked. */
export function RatingsPanel({ appId }: { appId: number }) {
	const [range, setRange] = useState<RangeKey>('90d')
	const [storefront, setStorefront] = useState<string | null>(null)
	const all = useQuery({
		queryKey: ['ratings', appId, range],
		queryFn: () => api.get<RatingRow[]>(`/api/v1/apps/${appId}/ratings${query({ from: rangeFrom(range) })}`),
	})
	const rows = all.data ?? []
	const latestByCountry = new Map<string, RatingRow>()
	for (const r of rows) latestByCountry.set(r.storefront, r)
	const countries = [...latestByCountry.values()].sort((a, b) => (b.ratingCount ?? 0) - (a.ratingCount ?? 0))
	const selected = storefront ?? countries[0]?.storefront ?? null
	const data = rows.filter((r) => r.storefront === selected)
	const priceChanges = data.filter((r, i) => i > 0 && r.price !== data[i - 1].price)
	const latest = data.at(-1)

	return (
		<section className="card stack">
			<div className="row" style={{ justifyContent: 'space-between' }}>
				<h2 style={{ margin: 0 }}>Ratings</h2>
				<div className="row">
					<select aria-label="Ratings country" value={selected ?? ''} onChange={(e) => setStorefront(e.target.value)}>
						{countries.map((c) => (
							<option key={c.storefront} value={c.storefront}>
								{c.storefront.toUpperCase()} · {formatCount(c.ratingCount)} ratings
							</option>
						))}
					</select>
					<RangePicker value={range} onChange={setRange} />
				</div>
			</div>
			{latest && (
				<p className="small muted" style={{ margin: 0 }}>
					<strong style={{ color: 'var(--text)', fontSize: '1.4rem' }}>{latest.ratingAvg?.toFixed(2) ?? '—'}</strong> ★
					from {formatCount(latest.ratingCount)} ratings ·{' '}
					{latest.price ? `${latest.price} ${latest.currency}` : 'Free'}
				</p>
			)}
			{data.length < 2 ? (
				<div className="empty">
					{all.isPending ? 'Loading…' : 'Ratings are collected daily; history appears after a couple of days.'}
				</div>
			) : (
				<ResponsiveContainer width="100%" height={240}>
					<LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
						<CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
						<XAxis dataKey="date" tickFormatter={(d) => formatDate(d)} stroke="var(--text-3)" fontSize={12} />
						<YAxis yAxisId="avg" domain={['auto', 'auto']} stroke="var(--text-3)" fontSize={12} width={40} />
						<YAxis
							yAxisId="count"
							orientation="right"
							stroke="var(--text-3)"
							fontSize={12}
							tickFormatter={(n) => Number(n).toLocaleString()}
							width={60}
						/>
						<Tooltip
							labelFormatter={(d) => formatDate(String(d))}
							contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}
						/>
						<Line
							yAxisId="avg"
							dataKey="ratingAvg"
							name="Average"
							stroke="#4f46e5"
							strokeWidth={2}
							dot={false}
							isAnimationActive={false}
						/>
						<Line
							yAxisId="count"
							dataKey="ratingCount"
							name="Ratings"
							stroke="#0d9488"
							strokeWidth={2}
							dot={false}
							isAnimationActive={false}
						/>
						{priceChanges.map((r) => (
							<ReferenceDot
								key={r.date}
								yAxisId="avg"
								x={r.date}
								y={r.ratingAvg ?? 0}
								r={5}
								fill="#d97706"
								stroke="none"
								label={{ value: `${r.price} ${r.currency}`, position: 'top', fontSize: 11 }}
							/>
						))}
					</LineChart>
				</ResponsiveContainer>
			)}
		</section>
	)
}
